import { usePage } from '@inertiajs/vue3';

const SKIP_TAGS = new Set([
    'A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'SCRIPT', 'STYLE',
    'CODE', 'PRE', 'SVG', 'IMG', 'VIDEO', 'AUDIO', 'IFRAME', 'CANVAS',
]);

const GLOSSARY_LINK_CLASS = 'glossary-link';

function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildPattern(terms) {
    if (!terms.length) return null;
    const alternatives = terms.map(t => escapeRegex(t.term)).join('|');
    return new RegExp(`\\b(${alternatives})\\b`, 'gi');
}

function shouldSkip(node) {
    let el = node.parentElement;
    while (el) {
        if (SKIP_TAGS.has(el.tagName)) return true;
        if (el.classList?.contains(GLOSSARY_LINK_CLASS)) return true;
        if (el.hasAttribute?.('data-no-glossary')) return true;
        el = el.parentElement;
    }
    return false;
}

function processElement(el, terms) {
    if (!terms?.length) return;

    const termMap = new Map();
    for (const t of terms) {
        termMap.set(t.term.toLowerCase(), t.slug);
    }

    const pattern = buildPattern(terms);
    if (!pattern) return;

    // Track which terms have already been linked (only link first occurrence)
    const linked = new Set();

    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            if (!node.textContent.trim()) return NodeFilter.FILTER_REJECT;
            if (shouldSkip(node)) return NodeFilter.FILTER_REJECT;
            return NodeFilter.FILTER_ACCEPT;
        },
    });

    const textNodes = [];
    while (walker.nextNode()) {
        textNodes.push(walker.currentNode);
    }

    for (const textNode of textNodes) {
        const text = textNode.textContent;
        pattern.lastIndex = 0;

        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = pattern.exec(text)) !== null) {
            const matchedTerm = match[0];
            const termKey = matchedTerm.toLowerCase();

            if (linked.has(termKey)) continue;

            const slug = termMap.get(termKey);
            if (!slug) continue;

            linked.add(termKey);

            if (match.index > lastIndex) {
                parts.push(document.createTextNode(text.slice(lastIndex, match.index)));
            }

            const link = document.createElement('a');
            link.href = `/glossar/${slug}`;
            link.className = GLOSSARY_LINK_CLASS;
            link.title = `${matchedTerm} – Definition im Glossar`;
            link.textContent = matchedTerm;
            parts.push(link);

            lastIndex = match.index + matchedTerm.length;
        }

        if (parts.length > 0) {
            if (lastIndex < text.length) {
                parts.push(document.createTextNode(text.slice(lastIndex)));
            }
            const fragment = document.createDocumentFragment();
            for (const part of parts) {
                fragment.appendChild(part);
            }
            textNode.parentNode.replaceChild(fragment, textNode);
        }
    }
}

let debounceTimer = null;

export const vGlossary = {
    // Vue's SSR renderer calls directive.getSSRProps() to compute
    // server-render attributes for each directive use. Without this,
    // SSR throws "Cannot read properties of undefined (reading
    // 'getSSRProps')" because the lookup chain hits an undefined
    // method. The glossary highlighter is a pure DOM-mutation effect
    // that only makes sense after hydration, so getSSRProps returns
    // an empty object (no extra attributes) — server output is just
    // the raw text, which the client-mounted directive then walks
    // and decorates with anchor tags.
    getSSRProps() {
        return {};
    },

    mounted(el) {
        const page = usePage();
        const terms = page.props.glossaryTerms;

        // Don't highlight on glossary pages themselves
        const path = window.location.pathname;
        if (path === '/glossar' || path.startsWith('/glossar/')) return;

        if (terms?.length) {
            // Slight delay to let the page render fully
            requestAnimationFrame(() => processElement(el, terms));
        }
    },

    updated(el) {
        const page = usePage();
        const terms = page.props.glossaryTerms;

        const path = window.location.pathname;
        if (path === '/glossar' || path.startsWith('/glossar/')) return;

        if (!terms?.length) return;

        // Debounce updates to avoid excessive processing
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            // Remove existing glossary links before re-processing
            const existing = el.querySelectorAll(`.${GLOSSARY_LINK_CLASS}`);
            for (const link of existing) {
                const text = document.createTextNode(link.textContent);
                link.parentNode.replaceChild(text, link);
            }
            // Normalize adjacent text nodes
            el.normalize();
            processElement(el, terms);
        }, 300);
    },
};
